import { supabase } from "@/integrations/supabase/client";
import type { RpcState } from "./types";

type PgError = { code?: string; message?: string } | null | undefined;

/** true si la RPC/tabla aún no está desplegada en esta BD (fallback elegante). */
export function isMissingObject(error: PgError): boolean {
  if (!error) return false;
  return (
    error.code === "PGRST202" ||
    error.code === "PGRST205" ||
    error.code === "42883" ||
    error.code === "42P01" ||
    /could not find the (function|table)|function .* does not exist|relation .* does not exist/i.test(
      error.message ?? "",
    )
  );
}

export function isForbidden(error: PgError): boolean {
  return !!error && (error.code === "42501" || /permission denied|sin acceso/i.test(error.message ?? ""));
}

/**
 * Llama una RPC del portal. Nunca lanza por "no desplegada" ni por 42501:
 * devuelve el `fallback` marcado con `pendiente` / `sinAcceso`.
 */
export async function callSocioRpc<T>(
  fn: string,
  params: Record<string, unknown> | undefined,
  normalize: (raw: unknown) => T,
  fallback: T,
): Promise<RpcState<T>> {
  const { data, error } = await (supabase as any).rpc(fn, params ?? {});
  if (error) {
    if (isMissingObject(error)) return { data: fallback, pendiente: true, sinAcceso: false };
    if (isForbidden(error)) return { data: fallback, pendiente: false, sinAcceso: true };
    throw error;
  }
  return { data: normalize(data), pendiente: false, sinAcceso: false };
}

export const num = (v: unknown, d = 0): number => {
  const n = typeof v === "number" ? v : v == null || v === "" ? NaN : Number(v);
  return Number.isFinite(n) ? n : d;
};

export const numOrNull = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : v == null || v === "" ? NaN : Number(v);
  return Number.isFinite(n) ? n : null;
};

export const strOrNull = (v: unknown): string | null =>
  v == null || v === "" ? null : String(v);

export const arr = (v: unknown): any[] => (Array.isArray(v) ? v : []);
